import { useEffect, useState } from "react";
import { apiClient } from "../apiClient";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const PAGE_SIZE = 15;

export default function ClientLedgerPage() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData = await apiClient.get("/clients/");
        setClients(clientsData);
      } catch (err) {
        setError("Failed to load clients");
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setTransactions([]);
      return;
    }
    const fetchLedger = async () => {
      try {
        setLedgerLoading(true);
        setError(null);
        const data = await apiClient.get(`/ledger/${selectedClientId}`);
        setTransactions(data);
      } catch (err) {
        setError(err.message || "Failed to load ledger");
      } finally {
        setLedgerLoading(false);
      }
    };
    fetchLedger();
  }, [selectedClientId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, selectedClientId]);

  const filteredEntries = transactions.filter(t => {
    if (dateFrom && t.date < dateFrom) return false;
    if (dateTo && t.date > dateTo) return false;
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => a.date.localeCompare(b.date));

  let runningBalance = 0;
  const entriesWithBalance = sortedEntries.map(t => {
    if (t.type === "charge") {
      runningBalance += t.amount_groszy;
    } else {
      runningBalance -= t.amount_groszy;
    }
    return { ...t, balance: runningBalance };
  });

  const totalDue = entriesWithBalance
    .filter(e => e.type === "charge")
    .reduce((sum, e) => sum + e.amount_groszy, 0) / 100;
  const totalPaid = entriesWithBalance
    .filter(e => e.type === "payment")
    .reduce((sum, e) => sum + e.amount_groszy, 0) / 100;
  const balance = totalDue - totalPaid;

  const clientStatus = balance <= 0 ? "paid" : totalPaid > 0 ? "partial" : "notPaid";

  const totalPages = Math.max(1, Math.ceil(entriesWithBalance.length / PAGE_SIZE));
  const pageEntries = entriesWithBalance.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const describeEntry = (entry) => {
    if (entry.source === "delivery_charge") return entry.notes || "Delivery charge";
    if (entry.source === "delivery_payment") return `Payment (${entry.payment_method || "unspecified"})`;
    if (entry.source === "manual_income") return entry.notes || "Manual income";
    return entry.notes || "Entry";
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  const exportToPDF = () => {
    if (entriesWithBalance.length === 0) return;
    const doc = new jsPDF();
    const client = clients.find(c => c.id === selectedClientId);
    const statusLabel = clientStatus === "paid" ? "Paid" : clientStatus === "partial" ? "Partial" : "Not Paid";

    doc.setFontSize(18);
    doc.setTextColor(45, 47, 94);
    doc.text("Pyszna Tortilla Bakery", 14, 20);
    doc.setFontSize(12);
    doc.text("Client Ledger Report", 14, 28);
    doc.setFontSize(10);
    doc.text(`Client: ${client?.name || "Unknown"}`, 14, 38);
    if (client?.address) doc.text(`Address: ${client.address}`, 14, 44);
    if (dateFrom || dateTo) doc.text(`Period: ${dateFrom || "All"} to ${dateTo || "Present"}`, 14, 50);

    doc.setFontSize(12);
    doc.text("Summary", 14, 60);
    doc.setFontSize(10);
    doc.text(`Total Due: ${totalDue.toFixed(2)} PLN`, 14, 68);
    doc.text(`Total Paid: ${totalPaid.toFixed(2)} PLN`, 14, 74);
    doc.text(`Balance: ${balance.toFixed(2)} PLN`, 14, 80);
    doc.text(`Status: ${statusLabel}`, 14, 86);

    const tableData = entriesWithBalance.map(item => [
      item.date,
      describeEntry(item),
      item.type === "charge" ? (item.amount_groszy / 100).toFixed(2) : "-",
      item.type === "payment" ? (item.amount_groszy / 100).toFixed(2) : "-",
      (item.balance / 100).toFixed(2)
    ]);

    autoTable(doc, {
      startY: 95,
      head: [["Date", "Description", "Debit (Due)", "Credit (Paid)", "Balance"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [45, 47, 94] },
      styles: { fontSize: 8 },
      columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
      }
    });

    doc.save(`${client?.name || "client"}_ledger_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const exportToExcel = () => {
    if (entriesWithBalance.length === 0) return;
    const client = clients.find(c => c.id === selectedClientId);

    const data = entriesWithBalance.map(item => ({
      Date: item.date,
      Description: describeEntry(item),
      Type: item.type === "charge" ? "Delivery" : "Payment",
      "Debit (PLN)": item.type === "charge" ? (item.amount_groszy / 100).toFixed(2) : "",
      "Credit (PLN)": item.type === "payment" ? (item.amount_groszy / 100).toFixed(2) : "",
      "Balance (PLN)": (item.balance / 100).toFixed(2)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    XLSX.writeFile(wb, `${client?.name || "client"}_ledger_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const exportToCSV = () => {
    if (entriesWithBalance.length === 0) return;
    const client = clients.find(c => c.id === selectedClientId);

    const headers = ["Date", "Description", "Type", "Debit (PLN)", "Credit (PLN)", "Balance (PLN)"];
    const rows = entriesWithBalance.map(item => [
      item.date,
      describeEntry(item),
      item.type === "charge" ? "Delivery" : "Payment",
      item.type === "charge" ? (item.amount_groszy / 100).toFixed(2) : "",
      item.type === "payment" ? (item.amount_groszy / 100).toFixed(2) : "",
      (item.balance / 100).toFixed(2)
    ]);

    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${client?.name || "client"}_ledger_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const filteredClients = clients.filter(client =>
    client.name && client.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const selectedClient = clients.find(c => c.id === selectedClientId);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 bg-[#F5E9D8] min-h-screen">
      <h2 className="text-2xl font-bold text-[#2D2F5E] text-center">
        📊 Client Ledger
      </h2>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <div className="relative">
          <label className="block text-sm font-medium text-[#2D2F5E] mb-1">
            Select Client
          </label>
          <input
            type="text"
            placeholder={selectedClient ? selectedClient.name : "Search client..."}
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setShowClientDropdown(true);
            }}
            onFocus={() => setShowClientDropdown(true)}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
          />
          {showClientDropdown && filteredClients.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-[#2D2F5E] border-opacity-30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => {
                    setSelectedClientId(client.id);
                    setClientSearch("");
                    setShowClientDropdown(false);
                  }}
                  className="p-2 hover:bg-[#F5E9D8] cursor-pointer text-[#2D2F5E] transition"
                >
                  <div className="font-medium">{client.name}</div>
                  {client.address && <div className="text-xs opacity-75">{client.address}</div>}
                </div>
              ))}
            </div>
          )}
          {selectedClient && (
            <div className="mt-2 text-sm text-[#2D2F5E]">
              Selected: <span className="font-medium">{selectedClient.name}</span>
              {selectedClient.address && <span className="text-xs"> – {selectedClient.address}</span>}
              <button
                type="button"
                onClick={() => {
                  setSelectedClientId(null);
                  setClientSearch("");
                }}
                className="ml-2 text-[#F76B4C] hover:text-[#E65A3C]"
              >
                ✕ Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedClientId && (
        <>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
            <h3 className="font-medium text-[#2D2F5E] mb-3 text-sm">📅 Date Range</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-[#2D2F5E] mb-1">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#2D2F5E] mb-1">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
                />
              </div>
            </div>
            <button onClick={clearFilters} className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded transition">Clear Filters</button>
          </div>

          {ledgerLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F76B4C]"></div>
              <p className="mt-2 text-[#2D2F5E] italic">Loading ledger...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 text-center">
                  <div className="text-sm text-[#2D2F5E] mb-1">Total Due</div>
                  <div className="text-2xl font-bold text-[#2D2F5E]">{totalDue.toFixed(2)} PLN</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 text-center">
                  <div className="text-sm text-[#2D2F5E] mb-1">Total Paid</div>
                  <div className="text-2xl font-bold text-green-600">{totalPaid.toFixed(2)} PLN</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 text-center">
                  <div className="text-sm text-[#2D2F5E] mb-1">Balance</div>
                  <div className={`text-2xl font-bold ${balance > 0 ? "text-red-600" : "text-green-600"}`}>
                    {balance.toFixed(2)} PLN
                  </div>
                </div>
              </div>

              {entriesWithBalance.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2">
                    📄 Export PDF
                  </button>
                  <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2">
                    📊 Export Excel
                  </button>
                  <button onClick={exportToCSV} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2">
                    📋 Export CSV
                  </button>
                </div>
              )}

              <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
                <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                  <h3 className="font-bold text-[#2D2F5E]">Transaction History ({entriesWithBalance.length})</h3>
                  <span className={`text-sm px-3 py-1 rounded font-medium ${
                    clientStatus === "paid" ? "bg-green-100 text-green-800" :
                    clientStatus === "partial" ? "bg-yellow-100 text-yellow-800" :
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {clientStatus === "paid" ? "✓ Fully Paid" :
                     clientStatus === "partial" ? "⚠ Partial Payment" : "○ Not Paid"}
                  </span>
                </div>
                {entriesWithBalance.length === 0 ? (
                  <p className="text-[#2D2F5E] italic text-center py-4">No transactions found for selected filters</p>
                ) : (
                  <>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <table className="w-full text-sm min-w-[560px]">
                        <thead>
                          <tr className="border-b-2 border-[#2D2F5E] border-opacity-20">
                            <th className="text-left p-2 text-[#2D2F5E]">Date</th>
                            <th className="text-left p-2 text-[#2D2F5E]">Description</th>
                            <th className="text-right p-2 text-[#2D2F5E]">Debit (Due)</th>
                            <th className="text-right p-2 text-green-700">Credit (Paid)</th>
                            <th className="text-right p-2 text-[#2D2F5E]">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageEntries.map((item) => (
                            <tr key={`${item.source}-${item.id}`} className="border-b border-[#2D2F5E] border-opacity-10">
                              <td className="p-2 text-[#2D2F5E] whitespace-nowrap">{item.date}</td>
                              <td className="p-2 text-[#2D2F5E]">
                                {describeEntry(item)}
                                <span className={`ml-2 text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                                  item.type === "charge" ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"
                                }`}>
                                  {item.type === "charge" ? "Delivery" : "Payment"}
                                </span>
                              </td>
                              <td className="p-2 text-right text-[#2D2F5E] font-medium whitespace-nowrap">
                                {item.type === "charge" ? (item.amount_groszy / 100).toFixed(2) : "-"}
                              </td>
                              <td className="p-2 text-right text-green-700 font-medium whitespace-nowrap">
                                {item.type === "payment" ? (item.amount_groszy / 100).toFixed(2) : "-"}
                              </td>
                              <td className={`p-2 text-right font-bold whitespace-nowrap ${
                                item.balance > 0 ? "text-red-600" : "text-green-600"
                              }`}>
                                {(item.balance / 100).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-3 border-t border-[#2D2F5E] border-opacity-10">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 rounded bg-[#2D2F5E] text-white text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          ← Prev
                        </button>
                        <span className="text-sm text-[#2D2F5E]">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 rounded bg-[#2D2F5E] text-white text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded text-center border border-red-200">
          ❌ {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F76B4C]"></div>
          <p className="mt-2 text-[#2D2F5E] italic">Loading...</p>
        </div>
      )}

      {!loading && !selectedClientId && (
        <p className="text-center text-[#2D2F5E] italic py-8">Select a client to view their ledger</p>
      )}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20 text-xs text-[#2D2F5E]">
        <p className="font-semibold mb-2">ℹ️ What this page shows:</p>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <li>Delivery charges for the selected client (from delivery items)</li>
          <li>Delivery payments recorded against those charges, including payment method (cash / BLIK / transfer)</li>
          <li>Manual income entries linked to this client, only if not already counted through a delivery payment</li>
        </ul>
        <p className="font-semibold mb-2">🚫 What this page does not show:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Legacy walk-in Sales or old-style Payments — these were part of the pre-V2 system and are excluded from the new ledger by design (data is safely backed up, not deleted)</li>
          <li>Driver-level cash reconciliation totals — a separate feature, not yet built</li>
        </ul>
      </div>
    </div>
  );
}